import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Inventory,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import CardImageWithPlaceholder from './CardImageWithPlaceholder';
import { TabGridLoading } from './TabLoadingSkeletons';
import PackFormDialog from './PackFormDialog';

interface Capability {
  title: string;
  image: string;
}

interface StudioPack {
  _id: string;
  title: string;
  image: string;
  description: string;
  capabilities: Capability[];
  totalClasses?: number;
  totalPrice?: number;
  pricePerClass?: number;
  isActive: boolean;
  createdAt: string;
  sessionType?: string;
  allowGuestBooking?: boolean;
  category?: { _id?: string; title?: string } | string | null;
  studio?: any;
}

interface StudioPacksTabProps {
  studioId: string;
  studioName?: string;
}

const StudioPacksTab: React.FC<StudioPacksTabProps> = ({ studioId, studioName }) => {
  const [packs, setPacks] = useState<StudioPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [packNote, setPackNote] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<StudioPack | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchPacks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getPacks({ studioId });
      const packsList =
        response.packs ||
        response.studioPacks ||
        response.data?.packs ||
        response.data?.studioPacks ||
        response.data ||
        [];
      setPacks(Array.isArray(packsList) ? packsList : []);
    } catch (err: any) {
      console.error('Error fetching studio packs:', err);
      setError(err.message || 'Failed to fetch packs');
      setPacks([]);
    } finally {
      setIsLoading(false);
    }
  }, [studioId]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, pack: StudioPack) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPack(pack);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setAnchorEl(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedPack) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      // Pinned to the pack's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deletePack(selectedPack._id, studioIdOf(selectedPack.studio) || studioId);
      await fetchPacks();
      setDeleteDialogOpen(false);
      setSelectedPack(null);
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete pack');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (pack: StudioPack) => {
    try {
      setToggling(true);
      setAnchorEl(null);
      await apiService.togglePackStatus(pack._id, !pack.isActive, studioIdOf(pack.studio) || studioId);
      await fetchPacks();
    } catch (err: any) {
      setError(err.message || 'Failed to update pack status');
    } finally {
      setToggling(false);
    }
  };

  const getCategoryLabel = (pack: StudioPack) => {
    if (!pack.category) return null;
    if (typeof pack.category === 'string') return null;
    return pack.category.title || null;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Studio Packs ({packs.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          size="small"
        >
          Create Pack
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <TabGridLoading count={6} variant="class" />
      ) : packs.length === 0 ? (
        <Card sx={{ border: `1px solid ${colors.divider}`, p: 4, textAlign: 'center' }}>
          <Inventory sx={{ fontSize: 48, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
          <Typography variant="body1" color="text.secondary">
            No packs found for this studio
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mt: 2 }}
            size="small"
          >
            Create Pack
          </Button>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {packs.map((pack) => {
            const categoryLabel = getCategoryLabel(pack);
            return (
              <Card
                key={pack._id}
                sx={{
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardImageWithPlaceholder
                  src={pack.image}
                  alt={pack.title}
                  height={160}
                  fallback={<Inventory sx={{ fontSize: 40, opacity: 0.35 }} />}
                />
                <CardContent>
                  <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="h6" fontWeight={600} noWrap>
                        {pack.title}
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        <Chip
                          label={pack.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: pack.isActive
                              ? `${colors.success}15`
                              : `${colors.error}15`,
                            color: pack.isActive ? colors.success : colors.error,
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 600,
                          }}
                        />
                        {pack.sessionType === 'private' && (
                          <Chip
                            label="Private"
                            size="small"
                            sx={{
                              backgroundColor: '#6A1B9A20',
                              color: '#6A1B9A',
                              fontSize: '0.7rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {pack.sessionType === 'duet' && (
                          <Chip
                            label="Duet"
                            size="small"
                            sx={{
                              backgroundColor: '#00838F20',
                              color: '#00838F',
                              fontSize: '0.7rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {pack.allowGuestBooking && (
                          <Chip
                            label="Guests"
                            size="small"
                            sx={{
                              backgroundColor: '#2E7D3220',
                              color: '#2E7D32',
                              fontSize: '0.7rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {categoryLabel && (
                          <Chip
                            label={categoryLabel}
                            size="small"
                            sx={{
                              backgroundColor: `${colors.secondary}15`,
                              color: colors.secondary,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, pack)}>
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {pack.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {pack.description}
                    </Typography>
                  )}

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Classes
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {pack.totalClasses ? pack.totalClasses : 'Unlimited'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Price
                      </Typography>
                      <Typography variant="body1" fontWeight={700} color={colors.primary}>
                        ₹{(pack.totalPrice ?? pack.pricePerClass ?? 0).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    {pack.capabilities && pack.capabilities.length > 0 && (
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        {pack.capabilities.slice(0, 2).map((cap, idx) => (
                          <Chip
                            key={idx}
                            label={cap.title}
                            size="small"
                            sx={{
                              backgroundColor: `${colors.primary}15`,
                              color: colors.primary,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        ))}
                        {pack.capabilities.length > 2 && (
                          <Chip
                            label={`+${pack.capabilities.length - 2}`}
                            size="small"
                            sx={{
                              backgroundColor: `${colors.textSecondary}15`,
                              color: colors.textSecondary,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <Edit sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedPack) handleToggleStatus(selectedPack);
          }}
          disabled={toggling}
        >
          {selectedPack?.isActive ? (
            <>
              <VisibilityOff sx={{ mr: 1, fontSize: 20 }} />
              Deactivate
            </>
          ) : (
            <>
              <Visibility sx={{ mr: 1, fontSize: 20 }} />
              Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: colors.error }}>
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      <PackFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          fetchPacks();
          setCreateDialogOpen(false);
        }}
        defaultStudioId={studioId}
        defaultStudioName={studioName}
      />

      {selectedPack && (
        <>
          <PackFormDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedPack(null);
            }}
            onSuccess={(note) => {
              fetchPacks();
              setEditDialogOpen(false);
              setSelectedPack(null);
              if (note) setPackNote(note);
            }}
            pack={selectedPack}
          />

          <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setDeleteError(null); }}>
            <DialogTitle>Delete Pack</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                Are you sure you want to delete the pack &quot;{selectedPack.title}&quot;? This
                action cannot be undone.
              </Typography>
              {deleteError && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDeleteError(null)}>
                  {deleteError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setDeleteDialogOpen(false); setDeleteError(null); }} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={16} /> : undefined}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Snackbar
        open={!!packNote}
        autoHideDuration={6000}
        onClose={() => setPackNote(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setPackNote(null)} sx={{ width: '100%' }}>
          {packNote}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudioPacksTab;
